
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, UserCheck } from "lucide-react";
import { TeamInvite } from "@/types/team";

// Define a simple interface to avoid deep type instantiation
interface InviteData {
  id: string;
  email: string;
  role: string;
  permissions: Record<string, boolean>;
  inviter_id: string;
  board_id?: string;
  token: string;
  inviter_name?: string;
  company_name?: string;
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isValidating, setIsValidating] = useState(true);
  const [userExists, setUserExists] = useState(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      toast.error("Link de convite inválido");
      navigate("/");
      return;
    }

    const validateInvite = async () => {
      try {
        setIsValidating(true);
        
        console.log("Validating invite with token:", token, "and email:", email);
        
        // Check if token is valid
        const { data: inviteData, error } = await supabase
          .from("team_invites")
          .select("*")
          .eq("token", token)
          .eq("email", email)
          .eq("accepted", false)
          .single();

        if (error || !inviteData) {
          console.error("Invite validation error:", error);
          throw new Error("Convite não encontrado ou já aceito");
        }

        console.log("Invite data found:", inviteData);

        // Get inviter details
        const { data: inviterData } = await supabase
          .from("users")
          .select("name, agency_name")
          .eq("id", inviteData.inviter_id)
          .single();

        console.log("Inviter data:", inviterData);

        // Convert to simple object to avoid deep type instantiation
        setInvite({
          id: inviteData.id,
          email: inviteData.email,
          role: inviteData.role,
          permissions: inviteData.permissions as Record<string, boolean>,
          inviter_id: inviteData.inviter_id,
          board_id: inviteData.board_id,
          token: inviteData.token,
          inviter_name: inviterData?.name || "Usuário",
          company_name: inviterData?.agency_name || "Empresa"
        });
        
        // Check if user already exists
        console.log("Checking if user exists with email:", email);
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("id, name, email")
          .eq("email", email)
          .maybeSingle();
        
        if (userData) {
          console.log("User already exists:", userData);
          setUserExists(true);
          // Auto-accept for existing users
          await handleExistingUser(inviteData, userData);
        } else {
          console.log("User does not exist, will need to create account");
          setUserExists(false);
        }
      } catch (error: any) {
        console.error("Error validating invite:", error);
        if (error.message !== "Convite não encontrado ou já aceito") {
          toast.error("Erro ao validar convite: " + error.message);
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateInvite();
  }, [token, email, navigate]);

  const handleExistingUser = async (inviteData: TeamInvite, userData: any) => {
    try {
      setIsLoading(true);
      console.log("Processing invite for existing user:", userData.id);
    
      // Update invite status first
      const { error: updateError } = await supabase
        .from("team_invites")
        .update({
          accepted: true,
          accepted_at: new Date().toISOString()
        })
        .eq("id", inviteData.id);
      
      if (updateError) {
        console.error("Error updating invite:", updateError);
        throw updateError;
      }
      
      console.log("Invite marked as accepted");
      
      // Handle board-specific or team invite
      if (inviteData.board_id) {
        console.log("Adding user to workflow board:", inviteData.board_id);
        
        // Check if user is already a member of this board
        const { data: existingMember } = await supabase
          .from("workflow_members")
          .select("id")
          .eq("user_id", userData.id)
          .eq("board_id", inviteData.board_id)
          .maybeSingle();
        
        if (!existingMember) {
          const { error: workflowError } = await supabase
            .from("workflow_members")
            .insert({
              user_id: userData.id,
              board_id: inviteData.board_id,
              created_by: inviteData.inviter_id
            });
          
          if (workflowError) {
            console.error("Error adding to workflow:", workflowError);
            throw new Error("Erro ao adicionar ao quadro de trabalho: " + workflowError.message);
          }
          
          console.log("User added to workflow successfully");
        } else {
          console.log("User is already a member of this board");
        }
          
        toast.success("Convite aceito! Você agora tem acesso ao quadro de trabalho.");
        navigate(`/workflow`);
      } else {
        console.log("Adding user as team member");
        
        // Check if user is already a team member
        const { data: existingTeamMember } = await supabase
          .from("team_members")
          .select("id")
          .eq("user_id", userData.id)
          .eq("added_by", inviteData.inviter_id)
          .maybeSingle();
        
        if (!existingTeamMember) {
          const { error: teamError } = await supabase
            .from("team_members")
            .insert({
              user_id: userData.id,
              added_by: inviteData.inviter_id,
              role: inviteData.role,
              permissions: inviteData.permissions
            });
          
          if (teamError) {
            console.error("Error adding team member:", teamError);
            throw teamError;
          }
          
          console.log("User added to team successfully");
        } else {
          console.log("User is already a team member");
        }
        
        // Add or update user_active_company
        const { error: companyError } = await supabase
          .from("user_active_company")
          .upsert({
            user_id: userData.id,
            company_id: inviteData.inviter_id,
            updated_at: new Date().toISOString()
          });
        
        if (companyError) {
          console.error("Error updating active company:", companyError);
          // Don't throw here as it's not critical
        }
        
        toast.success("Convite aceito! Você agora tem acesso à plataforma.");
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.error("Error handling existing user:", error);
      toast.error("Erro ao processar convite: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Creating new user account for:", email);

      // Create new user account
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: email!,
        password: password,
        options: {
          data: {
            name: name
          }
        }
      });

      if (signUpError) {
        console.error("Signup error:", signUpError);
        throw signUpError;
      }
      
      if (!signUpData.user) {
        throw new Error("Erro ao criar conta");
      }

      console.log("User account created:", signUpData.user.id);

      // Update user profile with name and type
      const { error: profileError } = await supabase
        .from('users')
        .upsert({ 
          id: signUpData.user.id,
          email: signUpData.user.email,
          user_type: 'invited',
          name: name 
        });
      
      if (profileError) {
        console.error("Error updating profile:", profileError);
        // Continue despite profile error
      }

      // Update invite status
      const { error: updateError } = await supabase
        .from("team_invites")
        .update({
          accepted: true,
          accepted_at: new Date().toISOString()
        })
        .eq("id", invite!.id);
      
      if (updateError) {
        console.error("Error updating invite:", updateError);
        throw updateError;
      }
        
      // Add the user as a workflow member if board_id exists
      if (invite!.board_id) {
        console.log("Adding new user to workflow board:", invite!.board_id);
        
        const { error: workflowError } = await supabase
          .from("workflow_members")
          .insert({
            user_id: signUpData.user.id,
            board_id: invite!.board_id,
            created_by: invite!.inviter_id
          });
        
        if (workflowError) {
          console.error("Error adding to workflow:", workflowError);
          throw new Error("Erro ao adicionar ao quadro de trabalho: " + workflowError.message);
        }
          
        toast.success("Conta criada com sucesso! Você já tem acesso ao quadro de trabalho.");
        navigate(`/workflow`);
      } else {
        console.log("Adding new user as team member");
        
        // Add as a team member if no board_id (general team invite)
        const { error: teamError } = await supabase
          .from("team_members")
          .insert({
            user_id: signUpData.user.id,
            added_by: invite!.inviter_id,
            role: invite!.role,
            permissions: invite!.permissions
          });
        
        if (teamError) {
          console.error("Error adding team member:", teamError);
          throw teamError;
        }

        // Add or update user_active_company
        const { error: companyError } = await supabase
          .from("user_active_company")
          .upsert({
            user_id: signUpData.user.id,
            company_id: invite!.inviter_id,
            updated_at: new Date().toISOString()
          });
        
        if (companyError) {
          console.error("Error updating active company:", companyError);
          // Don't throw here as it's not critical
        }
        
        toast.success("Conta criada com sucesso! Você já tem acesso à plataforma.");
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.error("Error accepting invite:", error);
      toast.error(error.message || "Erro ao processar convite");
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px] shadow-md">
          <CardHeader className="text-center">
            <CardTitle>Validando convite...</CardTitle>
            <CardDescription>Aguarde enquanto validamos seu convite.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px] shadow-md">
          <CardHeader className="text-center">
            <CardTitle>Convite inválido</CardTitle>
            <CardDescription>Este convite não é válido ou já foi aceito.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate("/")} className="w-full">
              Ir para a página inicial
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // If user exists and we're processing, show loading
  if (userExists && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px] shadow-md">
          <CardHeader className="text-center">
            <CardTitle>Processando convite...</CardTitle>
            <CardDescription>Adicionando você ao {invite.board_id ? 'quadro de trabalho' : 'time'}...</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user exists, show success message
  if (userExists) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px] shadow-md">
          <CardHeader className="text-center">
            <CardTitle>Convite processado!</CardTitle>
            <CardDescription>
              Você foi adicionado com sucesso. Redirecionando...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-6">
            <UserCheck className="h-12 w-12 text-green-500" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-[400px] shadow-md">
        <CardHeader className="text-center">
          <CardTitle>Aceitar convite</CardTitle>
          <CardDescription>
            <strong>{invite.inviter_name}</strong> da empresa <strong>{invite.company_name}</strong> lhe convidou para participar como <strong>{invite.role === 'admin' ? 'administrador' : invite.role === 'manager' ? 'gerente' : 'membro'}</strong>.
            {invite.board_id ? ' Você terá acesso a um quadro de trabalho específico.' : ' Crie sua conta para continuar.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAcceptInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email || ""}
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome completo"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Crie uma senha segura"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar senha</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua senha"
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <UserCheck className="mr-2 h-4 w-4" />
                  Criar conta e aceitar
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
